import { Injectable } from '@angular/core';
import { HttpClient, HttpEvent, HttpEventType } from '@angular/common/http';
import { catchError, map, Observable, retry } from 'rxjs';
import { Constants } from '../constant';
import { Common } from '../../shared/common';
import {
  Hostel, HostelBlock, HostelFloor,
  HostelRoom, HostelBed
} from '../models/hostel';

@Injectable()
export class HostelService {

  constructor(private http: HttpClient) { }

  private api = Constants.getApiEndpoint();

  // ─── Hostels ─────────────────────────────────────────────────────────────

  getAllHostels(stayIn: string = 'MAIN'): Observable<any> {
    return this.http.get<any>(`${this.api}/hostel/hostels/all`,
      Common.campusHeader(stayIn)).pipe(retry(1), catchError(Common.handleError));
  }

  getHostels(stayIn: string = 'MAIN', page = 0, size = 20, search = ''): Observable<any> {
    return this.http.get<any>(
      `${this.api}/hostel/hostels?page=${page}&size=${size}&search=${search}`,
      Common.campusHeader(stayIn)).pipe(retry(1), catchError(Common.handleError));
  }

  getHostelById(id: string): Observable<any> {
    return this.http.get<any>(`${this.api}/hostel/hostels/${id}`)
      .pipe(retry(1), catchError(Common.handleError));
  }

  saveHostel(hostel: Hostel): Observable<any> {
    if (hostel.id) {
      return this.http.put<any>(`${this.api}/hostel/hostels`, hostel, Common.httpOptions)
        .pipe(retry(1), catchError(Common.handleError));
    }
    return this.http.post<any>(`${this.api}/hostel/hostels`, hostel, Common.httpOptions)
      .pipe(retry(1), catchError(Common.handleError));
  }

  deleteHostel(id: string): Observable<any> {
    return this.http.delete<any>(`${this.api}/hostel/hostels/${id}`)
      .pipe(retry(1), catchError(Common.handleError));
  }

  // ─── Blocks ──────────────────────────────────────────────────────────────

  getBlocksByHostel(hostelId: string): Observable<any> {
    return this.http.get<any>(`${this.api}/hostel/blocks/by-hostel/${hostelId}`)
      .pipe(retry(1), catchError(Common.handleError));
  }

  getBlocks(stayIn: string = 'MAIN', hostelId = '', page = 0, size = 20): Observable<any> {
    return this.http.get<any>(
      `${this.api}/hostel/blocks?page=${page}&size=${size}&hostelId=${hostelId}`,
      Common.campusHeader(stayIn)).pipe(retry(1), catchError(Common.handleError));
  }

  saveBlock(block: HostelBlock): Observable<any> {
    if (block.id) {
      return this.http.put<any>(`${this.api}/hostel/blocks`, block, Common.httpOptions)
        .pipe(retry(1), catchError(Common.handleError));
    }
    return this.http.post<any>(`${this.api}/hostel/blocks`, block, Common.httpOptions)
      .pipe(retry(1), catchError(Common.handleError));
  }

  deleteBlock(id: string): Observable<any> {
    return this.http.delete<any>(`${this.api}/hostel/blocks/${id}`)
      .pipe(retry(1), catchError(Common.handleError));
  }

  // ─── Floors ──────────────────────────────────────────────────────────────

  getFloorsByBlock(blockId: string): Observable<any> {
    return this.http.get<any>(`${this.api}/hostel/floors/by-block/${blockId}`)
      .pipe(retry(1), catchError(Common.handleError));
  }

  getFloors(stayIn: string = 'MAIN', hostelId = '', blockId = '', page = 0, size = 20): Observable<any> {
    return this.http.get<any>(
      `${this.api}/hostel/floors?page=${page}&size=${size}&hostelId=${hostelId}&blockId=${blockId}`,
      Common.campusHeader(stayIn)).pipe(retry(1), catchError(Common.handleError));
  }

  saveFloor(floor: HostelFloor): Observable<any> {
    if (floor.id) {
      return this.http.put<any>(`${this.api}/hostel/floors`, floor, Common.httpOptions)
        .pipe(retry(1), catchError(Common.handleError));
    }
    return this.http.post<any>(`${this.api}/hostel/floors`, floor, Common.httpOptions)
      .pipe(retry(1), catchError(Common.handleError));
  }

  deleteFloor(id: string): Observable<any> {
    return this.http.delete<any>(`${this.api}/hostel/floors/${id}`)
      .pipe(retry(1), catchError(Common.handleError));
  }

  // ─── Rooms ───────────────────────────────────────────────────────────────

  getRoomsByFloor(floorId: string): Observable<any> {
    return this.http.get<any>(`${this.api}/hostel/rooms/by-floor/${floorId}`)
      .pipe(retry(1), catchError(Common.handleError));
  }

  getAvailableRooms(stayIn: string = 'MAIN'): Observable<any> {
    return this.http.get<any>(`${this.api}/hostel/rooms/available`,
      Common.campusHeader(stayIn)).pipe(retry(1), catchError(Common.handleError));
  }

  getRooms(stayIn: string = 'MAIN', hostelId = '', blockId = '', floorId = '',
    status = '', page = 0, size = 20): Observable<any> {
    let url = `${this.api}/hostel/rooms?page=${page}&size=${size}`;
    if (hostelId) url += `&hostelId=${hostelId}`;
    if (blockId) url += `&blockId=${blockId}`;
    if (floorId) url += `&floorId=${floorId}`;
    if (status) url += `&status=${status}`;
    return this.http.get<any>(url, Common.campusHeader(stayIn))
      .pipe(retry(1), catchError(Common.handleError));
  }

  saveRoom(room: HostelRoom): Observable<any> {
    if (room.id) {
      return this.http.put<any>(`${this.api}/hostel/rooms`, room, Common.httpOptions)
        .pipe(retry(1), catchError(Common.handleError));
    }
    return this.http.post<any>(`${this.api}/hostel/rooms`, room, Common.httpOptions)
      .pipe(retry(1), catchError(Common.handleError));
  }

  deleteRoom(id: string): Observable<any> {
    return this.http.delete<any>(`${this.api}/hostel/rooms/${id}`)
      .pipe(retry(1), catchError(Common.handleError));
  }

  // ─── Beds ────────────────────────────────────────────────────────────────

  getBedsByRoom(roomId: string): Observable<any> {
    return this.http.get<any>(`${this.api}/hostel/beds/by-room/${roomId}`)
      .pipe(retry(1), catchError(Common.handleError));
  }

  getVacantBeds(roomId: string): Observable<any> {
    return this.http.get<any>(`${this.api}/hostel/beds/vacant/${roomId}`)
      .pipe(retry(1), catchError(Common.handleError));
  }

  saveBed(bed: HostelBed): Observable<any> {
    if (bed.id) {
      return this.http.put<any>(`${this.api}/hostel/beds`, bed, Common.httpOptions)
        .pipe(retry(1), catchError(Common.handleError));
    }
    return this.http.post<any>(`${this.api}/hostel/beds`, bed, Common.httpOptions)
      .pipe(retry(1), catchError(Common.handleError));
  }

  deleteBed(id: string): Observable<any> {
    return this.http.delete<any>(`${this.api}/hostel/beds/${id}`)
      .pipe(retry(1), catchError(Common.handleError));
  }

  // ─── Bulk Upload ─────────────────────────────────────────────────────────

  // Bulk upload for Floors, Rooms & Beds infrastructure (single file/workbook)
  uploadBulkInfrastructure(
    formData: FormData
  ): Observable<HttpEvent<any>> {

    return this.http.post<any>(
      `${this.api}/hostel/hostels/bulk-upload/infrastructure`,
      formData,
      {
        reportProgress: true,
        observe: 'events'
      }
    );
  }

  // Bulk upload for Student Room Allocation
  uploadBulkAllocation(formData: FormData): Observable<HttpEvent<any>> {
    return this.http.post<any>(
      `${this.api}/hostel/hostels/bulk-upload/allocation`,
      formData,
      {
        reportProgress: true,
        observe: 'events'
      }
    ).pipe(
      catchError(Common.handleError)
    );
  }


  private getEventMessage(event: HttpEvent<any>) {
    switch (event.type) {
      case HttpEventType.UploadProgress:
        const percentDone = Math.round(100 * event.loaded / (event.total || 1));
        return { status: 'progress', percentDone };
      case HttpEventType.Response:
        return event.body;
      default:
        return { status: 'event', type: event.type };
    }
  }
}
